import React, { FunctionComponent, useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import AppTag from '../AppTag'
import Tag from '../../components/Tag'
import { IConnection, ITag } from '../../interfaces/models/connection'
import { useConnectionsHandlers } from '../../services/actions/connections/useConnectionsHandlers'
import Row from '../../components/Row'
import { useConnectionsStore } from '../../stores/connections/useConnectionsStore'
import SvgFragment from '../SvgFragment'
import { useTagsHandlers } from '../../services/actions/tags/useTagsHandlers'
import { useTagsStore } from '../../stores/tags/useTagsStore'
import { useAuthStore } from '../../stores/auth/useAuthStore'
import { styles } from '../../styles/themes/style'
import PrivateCheckBoxControl from '../PrivateCheckBoxControl';
import ColorSelectDropdownFragment from '../ColorSelectDropdownFragment';
import { useAppHandlers } from '../../services/actions/app/useAppHandlers';
import TooltipWrapper from '../../components/TooltipWrapper';
import TabsView from '../../components/TabsView';
import Checkbox from '../../components/Checkbox';

interface IApplyRemoveTagFormProps {
    connection: IConnection,
    tags: ITag[],
    onSaveHandler?: (e: any) => void,
    onKanbanTagChanged?: (hasKanbanTag: boolean, connection: IConnection) => void,
    overrideApplyRemove?: (connectionId: string, tagsToAdd: string[], tagsToRemove: string[]) => Promise<any>,
    dropdownBelow?: boolean,
    hideEmptyState?: boolean,
    collapseOverflow?: boolean,
    maxWidth?: number,
    showAddOnHover?: boolean,
    renderOnlyAddButton?: boolean,
    usePortal?: boolean,
    inboxSurface?: boolean
}

const ApplyRemoveTagForm: FunctionComponent<IApplyRemoveTagFormProps> = ({connection, tags, onSaveHandler, onKanbanTagChanged, overrideApplyRemove, dropdownBelow, hideEmptyState, collapseOverflow = false, maxWidth, showAddOnHover = false, renderOnlyAddButton = false, usePortal = false, inboxSurface = false}) => {
    const { store: { connectionFilterOptions } } = useConnectionsStore();
    const [showDropdown, setShowDropdown] = useState(false)
    const [search, setSearch] = useState('')
    const { applyRemoveTagsToConnection } = useConnectionsHandlers()
    const [dropdownTags, setDropdownTags] = useState<ITag[]>([])
    const [localTags, setLocalTags] = useState<ITag[]>(
        (tags && tags.length ? tags : (connection.tags || []).filter(tag => !tag.isForbidden))
    )
    const [queue, setQueue] = useState<any[]>([])
    const [processing, setProcessing] = useState(false)
    const queueLengthRef = useRef(0)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const addButtonRef = useRef<HTMLButtonElement>(null)
    const { createTagHandler, loadAllTagsHandler } = useTagsHandlers();
    const { store: { allTags: allTagsStore } } = useTagsStore();
    const { store: { user, workspaceOwner, workspace } } = useAuthStore();
    const { setCustomColorModalHandler } = useAppHandlers();
    const [newTagColor, setNewTagColor] = useState(styles.colors.primary600);
    const [newTagPrivate, setNewTagPrivate] = useState(true);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const measureRefs = useRef<(HTMLDivElement | null)[]>([]);
    const plusMeasureRef = useRef<HTMLDivElement>(null);
    const moreMeasureRef = useRef<HTMLDivElement>(null);
    const dropdownItemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const createButtonRef = useRef<HTMLButtonElement>(null);
    // using Tag component for measuring "+ N more" width; no label ref needed
    const [visibleCount, setVisibleCount] = useState<number | null>(() => (collapseOverflow ? 0 : null));
    const [showMoreDropdown, setShowMoreDropdown] = useState(false);
    const moreDropdownRef = useRef<HTMLDivElement>(null);
    const suppressCloseRef = useRef<boolean>(false);
    const customColorOpenRef = useRef<boolean>(false);
    // REVERT: Remove per-row ResizeObserver; rely on provided maxWidth from table

    // Portal state for dropdown positioning
    const [dropdownPosition, setDropdownPosition] = useState<{
        x: number;
        y: number;
        width: number;
        positioned: boolean;
        triggerRect?: { top: number, bottom: number, left: number, right: number, width: number, height: number };
    }>({ x: 0, y: 0, width: 300, positioned: false });
    const portalDropdownRef = useRef<HTMLDivElement | null>(null);

    // Tabs & applied state for dropdown
    const [activeTab, setActiveTab] = useState<'all' | 'applied'>('all');
    const [localAppliedIds, setLocalAppliedIds] = useState<Set<string>>(new Set<string>(
        (overrideApplyRemove ? (tags || []) : (tags && tags.length ? tags : (connection.tags || []).filter(tag => !tag.isForbidden))).map((t: any) => String(t._id))
    ));

    /**
     * When used in Inbox (`overrideApplyRemove`), backend responses and/or refetches can briefly
     * re-include tags that were just removed. Keep a local suppression set so removed tags
     * never "snap back" in the sidebar UI unless the user explicitly adds them again.
     */
    const suppressedTagIdsRef = useRef<Set<string>>(new Set<string>());

    // Only sync localTags with provided tags when queue is empty (fallback to connection.tags if props.tags empty)
    useEffect(() => {
        if (queue.length === 0) {
            const syncedTags = overrideApplyRemove
                ? (tags || [])
                : (tags && tags.length ? tags : (connection.tags || []).filter(tag => !tag.isForbidden));
            const suppressed = suppressedTagIdsRef.current
            const visibleSynced = syncedTags.filter((t: any) => !suppressed.has(String(t?._id)))
            setLocalTags(visibleSynced);
            setLocalAppliedIds(new Set<string>(visibleSynced.map((t: any) => String(t._id))));
        }
    }, [tags, connection, queue.length, overrideApplyRemove]);

    useEffect(() => {
        const fromFilters: any[] = connectionFilterOptions?.tagFilterOptions || [];
        const fromStore: any[] = allTagsStore || [];
        // Merge unique by _id
        const map = new Map<string, any>();
        [...fromStore, ...fromFilters].forEach((tag: any) => {
            if (tag && tag._id && !map.has(tag._id)) map.set(tag._id, tag);
        });
        let merged: any[] = Array.from(map.values());
        // Apply same visibility rule as TagTable: if not workspaceOwner, show only my tags
        if (!workspaceOwner) {
            merged = merged.filter((tag: any) => {
                // Show all public tags to all users, and private tags only to users who have access
                if (tag?.private === false) {
                    // Public tags are visible to all users
                    return true;
                } else {
                    // Private tags: check if user has access
                    // Support both formats: users can be array of strings OR array of objects with _id
                    return tag?.users && tag?.users.some((tagUser: any) => {
                        // If tagUser is a string, compare directly
                        if (typeof tagUser === 'string') {
                            return tagUser === user?._id;
                        }
                        // If tagUser is an object, compare _id property
                        return tagUser?._id === user?._id;
                    });
                }
            });
        }
        const localIds = new Set(localTags.map((t: any) => t?._id));
        const localTitles = new Set(localTags.map((t: any) => (t?.title || '').toLowerCase().trim()));
        const filtered = merged.filter((tag: any) => {
            const tagId = tag?._id;
            const tagTitle = (tag?.title || '').toLowerCase().trim();
            if (tag?.isForbidden) return false;
            // Exclude if already applied by id OR by title (covers optimistic create with temp id)
            return !localIds.has(tagId) && !localTitles.has(tagTitle);
        });
        const sorted = (filtered as ITag[]).slice().sort((a, b) => (a?.title || '').localeCompare(b?.title || ''));
        setDropdownTags(sorted);
    }, [connection, connectionFilterOptions, localTags, allTagsStore, workspaceOwner, user?._id])


    // Build All tags list (merged) sorted A->Z; do not exclude applied
    const allMergedSorted: ITag[] = useMemo(() => {
        const fromFilters: any[] = connectionFilterOptions?.tagFilterOptions || [];
        const fromStore: any[] = allTagsStore || [];
        const map = new Map<string, any>();
        [...fromStore, ...fromFilters].forEach((tag: any) => {
            if (tag && tag._id && !map.has(tag._id)) map.set(tag._id, tag);
        });
        let merged: any[] = Array.from(map.values());
        if (!workspaceOwner) {
            // Show all public tags to all users, and private tags only to users who have access
            merged = merged.filter((tag: any) => {
                // Public tags are visible to everyone
                if (tag?.private === false) return true;
                
                // Private tags: check if user has access
                // Support both formats: users can be array of strings OR array of objects with _id
                return tag?.users && tag?.users.some((tagUser: any) => {
                    // If tagUser is a string, compare directly
                    if (typeof tagUser === 'string') {
                        return tagUser === user?._id;
                    }
                    // If tagUser is an object, compare _id property
                    return tagUser?._id === user?._id;
                });
            });
        }
        return (merged as ITag[]).filter(t => !t.isForbidden).slice().sort((a, b) => (a?.title || '').localeCompare(b?.title || ''));
    }, [connectionFilterOptions, allTagsStore, workspaceOwner, user?._id]);

    const appliedTagsSorted: ITag[] = useMemo(() => {
        return (localTags || []).slice().sort((a, b) => (a?.title || '').localeCompare(b?.title || ''))
    }, [localTags]);

    // Map of applied kanban tags by pipeline (kanban id) for the current connection
    const appliedKanbanTagsByPipeline = useMemo(() => {
        const map = new Map<string, ITag>();
        (localTags || []).forEach((tag: any) => {
            if (tag?.forKanban && tag?.kanban) {
                const pipelineId = String(tag.kanban);
                if (!map.has(pipelineId)) {
                    map.set(pipelineId, tag);
                }
            }
        });
        return map;
    }, [localTags]);

    const filteredAllTags = useMemo(() => allMergedSorted.filter(tag => (tag?.title || '').toLowerCase().includes(search.toLowerCase())), [allMergedSorted, search]);
    const filteredAppliedTags = useMemo(() => appliedTagsSorted.filter(tag => (tag?.title || '').toLowerCase().includes(search.toLowerCase())), [appliedTagsSorted, search]);
    const listToRender: ITag[] = activeTab === 'all' ? filteredAllTags : filteredAppliedTags;
    const filteredTags = listToRender;

    // Check if we should show the create button
    const searchTrimmed = search.trim();
    const tagExists = searchTrimmed.length === 0 ? true : dropdownTags.some(tag => tag.title.toLowerCase() === searchTrimmed.toLowerCase()) || localTags.some(tag => tag.title.toLowerCase() === searchTrimmed.toLowerCase());
    const canCreateTag = searchTrimmed.length > 0 && searchTrimmed.length <= 30 && !tagExists;

    // Reset color and private when search changes (new tag intent)
    useEffect(() => {
        if (!canCreateTag) {
            setNewTagColor(styles.colors.primary600);
            setNewTagPrivate(true);
        }
    }, [canCreateTag, searchTrimmed]);

    // Reset focus when dropdown items change (e.g., after adding a tag)
    useEffect(() => {
        if (showDropdown) {
            setFocusedIndex(-1);
        }
    }, [filteredTags.length, canCreateTag, showDropdown]);

    // Initialize/clamp focusedIndex when dropdown items change
    useEffect(() => {
        if (!showDropdown) {
            setFocusedIndex(-1);
            return;
        }
        const totalItems = filteredTags.length + (canCreateTag ? 1 : 0);
        if (totalItems === 0) {
            setFocusedIndex(-1); // No items to focus
            return;
        }
        // Reset focus when items change (e.g., after adding a tag)
        setFocusedIndex(-1);
    }, [showDropdown, filteredTags.length, canCreateTag]);

    // Reset focus when dropdown opens/closes
    useEffect(() => {
        if (showDropdown) {
            if (!allTagsStore?.length) loadAllTagsHandler();
            setFocusedIndex(-1); // Start unfocused, let user navigate with arrows
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDropdown]);

    // Scroll focused item into view
    useEffect(() => {
        if (!showDropdown || focusedIndex === -1) return;
        const isCreateFocused = canCreateTag && focusedIndex === 0;
        if (isCreateFocused) {
            createButtonRef.current?.scrollIntoView({ block: 'nearest' });
        } else {
            const idx = canCreateTag ? focusedIndex - 1 : focusedIndex;
            dropdownItemRefs.current[idx]?.scrollIntoView({ block: 'nearest' });
        }
    }, [focusedIndex, showDropdown, canCreateTag]);

    // Calculate tag width based on title length - much more reliable
    useEffect(() => {

        if (!collapseOverflow || !maxWidth || !localTags?.length) {
            setVisibleCount(null);
            return;
        }
        
        let raf = 0;
        raf = requestAnimationFrame(() => {
            const plusWidth = plusMeasureRef.current?.offsetWidth || 30;
            
            // Use actual measured widths instead of estimates
            const tagWidths = localTags.map((tag, idx) => {
                const measuredElement = measureRefs.current[idx];
                
                if (measuredElement && measuredElement.offsetWidth > 0) {
                    return measuredElement.offsetWidth;
                }
                // Fallback to estimation if measurement not available
                const charWidth = 7.5;
                const padding = 10;
                const xButtonWidth = 16;
                const borderWidth = 2;
                return tag.title.length * charWidth + padding + xButtonWidth + borderWidth;
            });

            // Debug: detailed widths per tag and plus
            try {
                // left intentionally for future debug; no-op to avoid unused var
            } catch {}
            
            // Phase 1: Try to fit ALL tags + plus button (no "more" tag)
            const totalTagsWidth = tagWidths.reduce((sum, w) => sum + w, 0);
            
            // PRECISE gap calculation: (number of tags - 1) gaps between tags + 1 gap before plus
            const gapsBetweenTags = Math.max(0, localTags.length - 1) * 5;
            const gapBeforePlus = 5;
            const totalNeededForAll = totalTagsWidth + gapsBetweenTags + gapBeforePlus + plusWidth;
  
            if (totalNeededForAll <= maxWidth) {
                // Everything fits!
                setVisibleCount(localTags.length);
                return;
            }
            
            // Phase 2: Iteratively find max visible tags ensuring space for +N more and + button
            const n = localTags.length;
            const baseMoreWidth = moreMeasureRef.current?.offsetWidth || 80;
            const baseMoreTextLen = `${Math.max(1, n - 1)} more`.length;

            let chosenVisible = 1;

            for (let k = n; k >= 1; k--) {
                const hidden = n - k;
                // Compute required width for first k tags
                const tagsWidthK = tagWidths.slice(0, k).reduce((s, w) => s + w, 0);
                const gapsK = Math.max(0, k - 1) * 5;
                if (hidden === 0) {
                    // All tags visible: need space for plus only
                    const needed = tagsWidthK + gapsK + 5 + plusWidth; // gap before plus + plus
                    const fits = needed <= maxWidth;
                    if (fits) { chosenVisible = k; break; }
                    continue;
                }
                // Need +N more: estimate more width proportionally to text length
                const moreTextLen = `${hidden} more`.length;
                const moreWidth = Math.ceil(baseMoreWidth * (moreTextLen / baseMoreTextLen));
                const reserved = 5 + moreWidth + 5 + plusWidth; // gap + more + gap + plus
                const needed = tagsWidthK + gapsK + reserved;
                const fits = needed <= maxWidth;
                if (fits) { chosenVisible = k; break; }
            }

            setVisibleCount(chosenVisible);
        });
        return () => cancelAnimationFrame(raf);
    }, [collapseOverflow, maxWidth, localTags]);

    // Position dropdown when using portal
    useEffect(() => {
        if (!usePortal || !showDropdown || !addButtonRef.current) return;

        const computeAndSetPosition = () => {
            const buttonEl = addButtonRef.current;
            const portalEl = portalDropdownRef.current;
            if (!buttonEl || !portalEl) return;

            const buttonRect = buttonEl.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const gap = 10;

            // Find the cell container by looking for parent with position: relative
            let cellContainer: HTMLElement | null = null;
            let current: HTMLElement | null = buttonEl.parentElement;
            
            while (current && current !== document.body) {
                if (current.tagName === 'DIV') {
                    const computedStyle = window.getComputedStyle(current);
                    if (computedStyle.position === 'relative' && current.style.width) {
                        cellContainer = current;
                        break;
                    }
                }
                current = current.parentElement;
            }

            // Use cell container if found, otherwise use maxWidth as fallback
            // Limit dropdown width to maximum 350px
            const cellWidth = cellContainer 
                ? cellContainer.getBoundingClientRect().width 
                : (maxWidth || 300);
            const dropdownWidth = Math.min(Math.max(cellWidth, 300), 350);
            
            let desiredX = cellContainer 
                ? cellContainer.getBoundingClientRect().left 
                : buttonRect.left;
            
            // If dropdown would overflow right edge, adjust position
            if (desiredX + dropdownWidth > viewportWidth - gap) {
                desiredX = Math.max(gap, viewportWidth - dropdownWidth - gap);
            }
            
            // Ensure minimum gap from left edge
            desiredX = Math.max(gap, desiredX);

            // Vertical position - use button position for vertical alignment
            const menuHeight = portalEl.offsetHeight || 400;
            let desiredY = buttonRect.bottom + gap;
            const spaceBelow = viewportHeight - buttonRect.bottom;
            const spaceAbove = buttonRect.top;

            // If not enough space below, try above
            if (spaceBelow < menuHeight + gap && spaceAbove > menuHeight + gap) {
                desiredY = buttonRect.top - menuHeight - gap;
            } else if (spaceBelow < menuHeight + gap) {
                // If not enough space above either, position at bottom of viewport
                desiredY = Math.max(gap, viewportHeight - menuHeight - gap);
            }

            // Clamp vertically within viewport
            desiredY = Math.min(Math.max(gap, desiredY), viewportHeight - menuHeight - gap);

            setDropdownPosition(prev => {
                // Only update if position or width actually changed
                if (prev.x === desiredX && prev.y === desiredY && prev.width === dropdownWidth && prev.positioned) {
                    return prev;
                }
                return { 
                    x: desiredX, 
                    y: desiredY,
                    width: dropdownWidth,
                    positioned: true,
                    triggerRect: { 
                        top: buttonRect.top, 
                        bottom: buttonRect.bottom, 
                        left: buttonRect.left, 
                        right: buttonRect.right, 
                        width: buttonRect.width, 
                        height: buttonRect.height 
                    }
                };
            });
        };

        // Initial position calculation
        const rafId = requestAnimationFrame(computeAndSetPosition);

        // Recompute on resize/scroll
        const handleResize = () => computeAndSetPosition();
        const handleScroll = () => computeAndSetPosition();

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll, true);

        // Recompute on dropdown size changes
        let ro: ResizeObserver | null = null;
        if (portalDropdownRef.current && 'ResizeObserver' in window) {
            ro = new ResizeObserver(() => computeAndSetPosition());
            ro.observe(portalDropdownRef.current);
        }

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll, true);
            if (ro) ro.disconnect();
        };
    }, [usePortal, showDropdown, maxWidth]);

    // Focus the portal dropdown when it opens (for keyboard navigation)
    useEffect(() => {
        if (usePortal && showDropdown && portalDropdownRef.current && dropdownPosition.positioned) {
            // Use a small delay to ensure the portal is fully rendered
            const timer = setTimeout(() => {
                portalDropdownRef.current?.focus();
            }, 10);
            return () => clearTimeout(timer);
        }
    }, [usePortal, showDropdown, dropdownPosition.positioned]);

    // Close dropdown on outside click
    useEffect(() => {
        if (!showDropdown) return;
        function handleClickOutside(event: MouseEvent) {
            if (customColorOpenRef.current) {
                // keep dropdown open while custom color modal is active
                return;
            }
            if (suppressCloseRef.current) {
                suppressCloseRef.current = false;
                return;
            }
            const clickedElement = event.target as Node;
            const isInsideDropdown = usePortal 
                ? (portalDropdownRef.current && portalDropdownRef.current.contains(clickedElement))
                : (dropdownRef.current && dropdownRef.current.contains(clickedElement));
            const isInsideButton = addButtonRef.current && addButtonRef.current.contains(clickedElement);
            
            if (!isInsideDropdown && !isInsideButton) {
                setShowDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDropdown, usePortal]);

    // Close more dropdown on outside click
    useEffect(() => {
        if (!showMoreDropdown) return;
        function handleClickOutside(event: MouseEvent) {
            if (
                moreDropdownRef.current &&
                !moreDropdownRef.current.contains(event.target as Node)
            ) {
                setShowMoreDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showMoreDropdown]);

    // Keep queueLengthRef in sync
    useEffect(() => {
        queueLengthRef.current = queue.length;
    }, [queue.length]);

    // Queue processing effect
    useEffect(() => {
        if (!processing && queue.length > 0) {
            processQueue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queue, processing]);

    const processQueue = async () => {
        if (processing || queue.length === 0) return;
        setProcessing(true);
        const action = queue[0];
        let apiFailed = false;
        let result: any = null;
        try {
            if (action.type === 'add') {
                const applyTags = [action.tag._id];
                const removeTags: any[] = [];
                if (overrideApplyRemove) {
                    await overrideApplyRemove(connection._id, applyTags, removeTags);
                } else {
                    result = await applyRemoveTagsToConnection(connection._id, applyTags, removeTags);
                    onSaveHandler && onSaveHandler(result);
                }
            } else if (action.type === 'remove') {
                const removeTags = [action.tag._id];
                const applyTags: any[] = [];
                if (overrideApplyRemove) {
                    await overrideApplyRemove(connection._id, applyTags, removeTags);
                } else {
                    result = await applyRemoveTagsToConnection(connection._id, applyTags, removeTags);
                    onSaveHandler && onSaveHandler(result);
                }
            } else if (action.type === 'removeAll') {
                const removeTags = action.tagIds || [];
                const applyTags: any[] = [];
                if (overrideApplyRemove) {
                    await overrideApplyRemove(connection._id, applyTags, removeTags);
                } else {
                    result = await applyRemoveTagsToConnection(connection._id, applyTags, removeTags);
                    onSaveHandler && onSaveHandler(result);
                }
            } else if (action.type === 'create') {
                // 1. Create tag
                const payload = {
                    title: action.title,
                    color: action.color || styles.colors.primary600,
                    private: action.private !== undefined ? action.private : true
                };
                // Pass false for both displayToast and refresh to add tag optimistically to store
                const createdTag = await createTagHandler(payload, false, false);
                if (!createdTag || !(createdTag as any)._id) throw new Error('Failed to create tag');
                // 2. Assign tag
                const applyTags = [(createdTag as any)._id];
                const removeTags: any[] = [];
                if (overrideApplyRemove) {
                    await overrideApplyRemove(connection._id, applyTags, removeTags);
                } else {
                    result = await applyRemoveTagsToConnection(connection._id, applyTags, removeTags);
                    onSaveHandler && onSaveHandler(result);
                }
            }
        } catch (e) {
            apiFailed = true;
        }
        if (apiFailed) {
            setLocalTags(tags => {
                if (action.type === 'add') {
                    return tags.filter(t => t._id !== action.tag._id);
                } else if (action.type === 'remove') {
                    return [...tags, action.tag];
                } else if (action.type === 'removeAll') {
                    return action.previousTags || tags;
                } else if (action.type === 'create') {
                    return tags.filter(t => t.title !== action.title);
                }
                return tags;
            });
            console.error('Failed to update tag. Please try again.');
        } else {
            // Check if kanban tag status changed and notify parent
            if (onKanbanTagChanged) {
                const hasKanbanTag = result?.tags?.some((tag: any) => tag.forKanban);
                const hadKanbanTag = localTags.some((tag: any) => tag.forKanban);
                
                if (hasKanbanTag !== hadKanbanTag) {
                    onKanbanTagChanged(hasKanbanTag, result);
                }
            }
        }
        setQueue(q => q.slice(1));
        setProcessing(false);
    };

    // Optimistic remove
    const handleRemoveTag = useCallback((tag: ITag) => {
        suppressedTagIdsRef.current.add(String((tag as any)?._id))
        setLocalTags(tags => tags.filter(t => t._id !== tag._id));
        setLocalAppliedIds(prev => { const next = new Set(Array.from(prev)); next.delete(String(tag._id)); return next; });
        setQueue(q => [...q, { type: 'remove', tag }]);
    }, []);

    // Optimistic add
    const handleAddTag = useCallback((tag: ITag) => {
        suppressedTagIdsRef.current.delete(String((tag as any)?._id))
        setLocalTags(tags => [...tags, tag]);
        setLocalAppliedIds(prev => { const next = new Set(Array.from(prev)); next.add(String(tag._id)); return next; });
        setQueue(q => [...q, { type: 'add', tag }]);
    }, []);

    const onToggleTag = useCallback((tag: ITag, checked: boolean) => {
        checked ? handleAddTag(tag) : handleRemoveTag(tag);
    }, [handleAddTag, handleRemoveTag]);

    const onRemoveAll = useCallback(() => {
        const ids = localTags.map((t: any) => String(t._id));
        if (ids.length === 0) return;
        const prev = localTags.slice();
        ids.forEach((id) => suppressedTagIdsRef.current.add(String(id)))
        setLocalTags([]);
        setLocalAppliedIds(new Set());
        setQueue(q => [...q, { type: 'removeAll', tagIds: ids, previousTags: prev }]);
    }, [localTags]);

    // Helper function to render dropdown content
    const renderDropdownContent = () => (
        <>
            <DropdownSearchInput
                type="text"
                placeholder="Search tags..."
                value={search}
                autoFocus
                maxLength={30}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
                onKeyDown={handleKeyDown}
            />
            {canCreateTag && (
                <CreateWrapper>
                    <Row>
                        <ColorSelectDropdownFragment
                            color={newTagColor}
                            onColorChangedHandler={setNewTagColor}
                            openCustomColorModalHandler={openCustomColorModalHandler}
                            label="Background color"
                            showFocusStyle={true}
                        />
                    </Row>
                    {workspace?.isBusiness && (
                        <Row>
                            <PrivateCheckBoxControl
                                type='tag'
                                value={newTagPrivate}
                                onChangeHandler={setNewTagPrivate}
                            />
                        </Row>
                    )}
                    <Row>
                        <CreateButton style={{ marginTop: 4 }} ref={createButtonRef as any} onClick={handleCreateTag} onMouseEnter={() => setFocusedIndex(0)}>
                            Create tag
                        </CreateButton>
                    </Row>
                </CreateWrapper>
            )}
            <TabsView tabs={[{ slug: 'all', title: 'All tags', selected: activeTab === 'all' }, { slug: 'applied', title: 'Applied tags', selected: activeTab === 'applied' }]} onSwitchTab={(slug: any) => setActiveTab(slug)} borderBottom={false} showUnderline={true} inactiveColor={styles.colors.black300} rightContent={<RemoveAllAction onClick={() => onRemoveAll()} disabled={appliedTagsSorted.length === 0}>Untag all</RemoveAllAction>} />
            <DropdownList>
                {filteredTags.length === 0 ? (
                    <DropdownItem>No tags to show</DropdownItem>
                ) : (
                    filteredTags.map((tag, index) => {
                        const checked = localAppliedIds.has(String(tag._id));

                        // Disable kanban column tags from pipelines that already have another column tag applied
                        let disabled = false;
                        if (tag?.forKanban && (tag as any)?.kanban) {
                            const pipelineId = String((tag as any).kanban);
                            const appliedForPipeline = appliedKanbanTagsByPipeline.get(pipelineId);
                            if (appliedForPipeline && String(appliedForPipeline._id) !== String(tag._id)) {
                                disabled = true;
                            }
                        }

                        const itemIndex = (canCreateTag ? 1 : 0) + index;

                        return (
                            <TagRowItem
                                key={tag._id}
                                ref={(el: HTMLDivElement | null) => {
                                    dropdownItemRefs.current[index] = el;
                                }}
                                onMouseEnter={() => setFocusedIndex(itemIndex)}
                                onClick={() => {
                                    if (!disabled) onToggleTag(tag, !checked);
                                }}
                                style={{
                                    background:
                                        focusedIndex === itemIndex ? "#f0f3ff" : undefined,
                                    cursor: disabled ? "not-allowed" : "pointer",
                                    opacity: disabled ? 0.5 : 1,
                                }}
                            >
                                <Checkbox
                                    checked={checked}
                                    checkboxId={tag._id}
                                    onClickHandler={(e) => {
                                        e.stopPropagation();
                                        if (!disabled) onToggleTag(tag, !checked);
                                    }}
                                    $height="24px"
                                    disabled={disabled}
                                />
                                <AppTag
                                    tag={tag}
                                    tooltipMessage={
                                        disabled
                                            ? "One tag per pipeline is allowed."
                                            : undefined
                                    }
                                />
                            </TagRowItem>
                        );
                    })
                )}
            </DropdownList>
        </>
    );

    // Handle create new tag
    const handleCreateTag = useCallback(() => {
        if (!canCreateTag) return;
        // Generate a temporary optimistic tag (no _id)
        const optimisticTag = {
            _id: 'temp-' + Date.now(),
            title: searchTrimmed,
            color: newTagColor,
            isForbidden: false,
            private: newTagPrivate,
            createdAt: '',
            connectionsCount: 0
        };
        setLocalTags(tags => [...tags, optimisticTag]);
        setQueue(q => [...q, { type: 'create', title: searchTrimmed, color: newTagColor, private: newTagPrivate }]);
        setSearch('');
        setNewTagColor(styles.colors.primary600);
        setNewTagPrivate(true);
    }, [canCreateTag, searchTrimmed, newTagColor, newTagPrivate]);

    // Handler for opening the custom color modal
    const openCustomColorModalHandler = useCallback(() => {
        // keep dropdown open while interacting with custom color modal
        customColorOpenRef.current = true;
        setCustomColorModalHandler(newTagColor, (color: string) => {
            if (!color.startsWith('#')) {
                setNewTagColor('#' + color);
            } else {
                setNewTagColor(color);
            }
            // modal closed after save; allow dropdown to close on future outside clicks
            customColorOpenRef.current = false;
        });
    }, [newTagColor, setCustomColorModalHandler]);

    // Keyboard navigation handler
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (!showDropdown) return;
        
        const total = filteredTags.length + (canCreateTag ? 1 : 0);
        if (total === 0) return;

        switch (e.key) {
            case 'Escape':
                e.preventDefault();
                e.stopPropagation();
                setShowDropdown(false);
                setFocusedIndex(-1);
                break;
            case 'ArrowDown':
                e.preventDefault();
                e.stopPropagation();
                setFocusedIndex(prev => prev < 0 ? 0 : (prev + 1) % total);
                break;
            case 'ArrowUp':
                e.preventDefault();
                e.stopPropagation();
                setFocusedIndex(prev => prev < 0 ? total - 1 : (prev - 1 + total) % total);
                break;
            case 'Enter':
                e.preventDefault();
                e.stopPropagation();
                if (focusedIndex >= 0) {
                    const isCreate = canCreateTag && focusedIndex === 0;
                    if (isCreate) {
                        handleCreateTag();
                    } else {
                        const idx = canCreateTag ? focusedIndex - 1 : focusedIndex;
                        const tag = filteredTags[idx];
                        if (tag) {
                            const checked = localAppliedIds.has(String(tag._id));
                            checked ? handleRemoveTag(tag) : handleAddTag(tag);
                        }
                    }
                } else if (canCreateTag) {
                    // If no focus but can create tag, create it
                    handleCreateTag();
                }
                break;
        }
    }, [showDropdown, filteredTags, canCreateTag, focusedIndex, handleCreateTag, handleAddTag, localAppliedIds, handleRemoveTag]);

    const totalTagsCount = localTags.length;
    const renderVisibleCount = collapseOverflow && maxWidth ? (visibleCount ?? totalTagsCount) : totalTagsCount;

    if (renderOnlyAddButton) {
        return (
            <WidthLimiter $width={maxWidth}>
                <TagRow $nowrap $hoverAdd={!!showAddOnHover} $dropdownOpen={showDropdown} $fullWidth>
                    <TooltipWrapper placement="top" tooltipText="Add new tag">
                        <AddTagButton className="add-tag-button" ref={addButtonRef} onClick={(e) => { e.stopPropagation(); setShowDropdown(v => !v); }}>
                            <SvgFragment type="plusTags" />
                        </AddTagButton>
                    </TooltipWrapper>
                    {showDropdown && (
                        usePortal ? (
                            createPortal(
                                <PortalDropdownMenu
                                    ref={portalDropdownRef}
                                    style={{
                                        position: 'fixed',
                                        top: `${dropdownPosition.y}px`,
                                        left: `${dropdownPosition.x}px`,
                                        width: `${dropdownPosition.width}px`,
                                        zIndex: 10050,
                                        visibility: dropdownPosition.positioned ? 'visible' : 'hidden',
                                        pointerEvents: dropdownPosition.positioned ? 'auto' : 'none'
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={handleKeyDown}
                                    tabIndex={0}
                                >
                                    {renderDropdownContent()}
                                </PortalDropdownMenu>,
                                document.body
                            )
                        ) : (
                            <DropdownMenu ref={dropdownRef} $bulk={false} $below={dropdownBelow} $fullWidth onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown} tabIndex={0}>
                                {renderDropdownContent()}
                            </DropdownMenu>
                        )
                    )}
                </TagRow>
            </WidthLimiter>
        )
    }

    return (
        <WidthLimiter $width={maxWidth}>
        <TagRow $nowrap={collapseOverflow} $hoverAdd={!!showAddOnHover} $dropdownOpen={showDropdown} $inboxTagSurface={inboxSurface && localTags.length > 0}>
            {localTags.length === 0 ? (
                hideEmptyState ? (
                    <>
                        <TooltipWrapper placement="top" tooltipText="Add new tag">
                            <AddTagButton className="add-tag-button" ref={addButtonRef} onClick={(e) => { e.stopPropagation(); setShowDropdown(v => !v); }}>
                                <SvgFragment type="plusTags" />
                            </AddTagButton>
                        </TooltipWrapper>
                        {showDropdown && (
                            usePortal ? (
                                createPortal(
                                    <PortalDropdownMenu
                                        ref={portalDropdownRef}
                                        style={{
                                            position: 'fixed',
                                            top: `${dropdownPosition.y}px`,
                                            left: `${dropdownPosition.x}px`,
                                            width: `${dropdownPosition.width}px`,
                                            zIndex: 10050,
                                            visibility: dropdownPosition.positioned ? 'visible' : 'hidden',
                                            pointerEvents: dropdownPosition.positioned ? 'auto' : 'none'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={handleKeyDown}
                                        tabIndex={0}
                                    >
                                        {renderDropdownContent()}
                                    </PortalDropdownMenu>,
                                    document.body
                                )
                            ) : (
                                <DropdownMenu ref={dropdownRef} $bulk={false} $below={dropdownBelow} onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown} tabIndex={0}>
                                    {renderDropdownContent()}
                                </DropdownMenu>
                            )
                        )}
                    </>
                ) : (
                    <NoTagsWrapper $inboxSurface={inboxSurface}>
                        <NoTagsLabel>No tags to show</NoTagsLabel>
                        <AddTagEmptyButton onClick={(e) => { e.stopPropagation(); setShowDropdown(v => !v); }}>Add tag</AddTagEmptyButton>
                        {showDropdown && (
                            usePortal ? (
                                createPortal(
                                    <PortalDropdownMenu
                                        ref={portalDropdownRef}
                                        style={{
                                            position: 'fixed',
                                            top: `${dropdownPosition.y}px`,
                                            left: `${dropdownPosition.x}px`,
                                            width: `${dropdownPosition.width}px`,
                                            zIndex: 10050,
                                            visibility: dropdownPosition.positioned ? 'visible' : 'hidden',
                                            pointerEvents: dropdownPosition.positioned ? 'auto' : 'none'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={handleKeyDown}
                                        tabIndex={0}
                                    >
                                        {renderDropdownContent()}
                                    </PortalDropdownMenu>,
                                    document.body
                                )
                            ) : (
                                <DropdownMenu ref={dropdownRef} $bulk={false} $below={dropdownBelow} onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown} tabIndex={0}>
                                    <DropdownSearchInput
                                    type="text"
                                    placeholder="Search tags..."
                                    value={search}
                                    autoFocus
                                    maxLength={30}
                                    onChange={e => setSearch(e.target.value)}
                                    onClick={e => e.stopPropagation()}
                                    onKeyDown={handleKeyDown}
                                />
                                {canCreateTag && (
                                    <CreateWrapper>
                                        <Row>
                                            <ColorSelectDropdownFragment
                                                color={newTagColor}
                                                onColorChangedHandler={setNewTagColor}
                                                openCustomColorModalHandler={openCustomColorModalHandler}
                                                label="Background color"
                                                showFocusStyle={true}
                                            />
                                        </Row>
                                        {workspace?.isBusiness && (
                                            <Row>
                                                <PrivateCheckBoxControl
                                                    type='tag'
                                                    value={newTagPrivate}
                                                    onChangeHandler={setNewTagPrivate}
                                                />
                                            </Row>
                                        )}
                                        <Row>
                                            <CreateButton style={{ marginTop: 4 }} ref={createButtonRef as any} onClick={handleCreateTag} onMouseEnter={() => setFocusedIndex(0)}>
                                                Create tag
                                            </CreateButton>
                                        </Row>
                                    </CreateWrapper>
                                )}
                                <TabsView tabs={[{ slug: 'all', title: 'All tags', selected: activeTab === 'all' }, { slug: 'applied', title: 'Applied tags', selected: activeTab === 'applied' }]} onSwitchTab={(slug: any) => setActiveTab(slug)} borderBottom={false} showUnderline={true} inactiveColor={styles.colors.black300} rightContent={<RemoveAllAction onClick={() => onRemoveAll()} disabled={appliedTagsSorted.length === 0}>Untag all</RemoveAllAction>} />
                                <DropdownList>
                                    {filteredTags.length === 0 ? (
                                        <DropdownItem>No tags to show</DropdownItem>
                                    ) : (
                                        filteredTags.map((tag, index) => {
                                            const checked = localAppliedIds.has(String(tag._id));

                                            // Disable kanban column tags from pipelines that already have another column tag applied
                                            let disabled = false;
                                            if (tag?.forKanban && (tag as any)?.kanban) {
                                                const pipelineId = String((tag as any).kanban);
                                                const appliedForPipeline = appliedKanbanTagsByPipeline.get(pipelineId);
                                                if (appliedForPipeline && String(appliedForPipeline._id) !== String(tag._id)) {
                                                    disabled = true;
                                                }
                                            }

                                            const itemIndex = (canCreateTag ? 1 : 0) + index;

                                            return (
                                                <TagRowItem
                                                    key={tag._id}
                                                    ref={(el: HTMLDivElement | null) => {
                                                        dropdownItemRefs.current[index] = el;
                                                    }}
                                                    onMouseEnter={() => setFocusedIndex(itemIndex)}
                                                    onClick={() => {
                                                        if (!disabled) onToggleTag(tag, !checked);
                                                    }}
                                                    style={{
                                                        background:
                                                            focusedIndex === itemIndex ? "#f0f3ff" : undefined,
                                                        cursor: disabled ? "not-allowed" : "pointer",
                                                        opacity: disabled ? 0.5 : 1,
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={checked}
                                                        checkboxId={tag._id}
                                                        onClickHandler={(e) => {
                                                            e.stopPropagation();
                                                            if (!disabled) onToggleTag(tag, !checked);
                                                        }}
                                                        $height="24px"
                                                        disabled={disabled}
                                                    />
                                                    <AppTag tag={tag} />
                                                </TagRowItem>
                                            );
                                        })
                                    )}
                                </DropdownList>
                            </DropdownMenu>
                            )
                        )}
                    </NoTagsWrapper>
                )
            ) : (
                <>
                    {/* Hidden tags for measurement */}
                    {collapseOverflow && maxWidth ? (
                        <HiddenMeasure>
                            {/* tag widths */}
                            {localTags.map((tag, idx) => (
                                <div key={`m-${tag._id}`} ref={el => measureRefs.current[idx] = el}>
                                    <AppTag tag={tag} />
                                </div>
                            ))}
                            {/* dynamic + button width */}
                            <div ref={plusMeasureRef}>
                                <AddTagButton className="add-tag-button">
                                    <SvgFragment type="plusTags" />
                                </AddTagButton>
                            </div>
                            {/* dynamic more chip width */}
                            <div ref={moreMeasureRef}>
                                <Tag className="connection-tags-class" title={`${Math.max(1, localTags.length - 1)} more`} />
                            </div>
                        </HiddenMeasure>
                    ) : null}

                    {(collapseOverflow && maxWidth ? localTags.slice(0, renderVisibleCount) : localTags).map(tag => (
                        <div key={tag._id} onClick={(e) => e.stopPropagation()}>
                            <AppTag
                                tag={tag}
                                onDestructiveClickHandler={() => handleRemoveTag(tag)}
                            />
                        </div>
                    ))}
                    {collapseOverflow && maxWidth && (totalTagsCount - renderVisibleCount) > 0 && (
                        <MoreTagWrapper>
                            <div onClick={(e) => { e.stopPropagation(); setShowMoreDropdown(v => !v); }}>
                                <Tag
                                    className="connection-tags-class"
                                    title={`${totalTagsCount - renderVisibleCount} more`}
                                />
                            </div>
                            {showMoreDropdown && (
                                <MoreDropdown ref={moreDropdownRef} onClick={(e) => e.stopPropagation()}>
                                    <MoreDropdownList>
                                        {localTags.slice(renderVisibleCount).map(tag => (
                                            <MoreDropdownItem key={tag._id}>
                                                <AppTag
                                                    tag={tag}
                                                    onDestructiveClickHandler={() => handleRemoveTag(tag)}
                                                />
                                            </MoreDropdownItem>
                                        ))}
                                    </MoreDropdownList>
                                </MoreDropdown>
                            )}
                        </MoreTagWrapper>
                    )}
                    <TooltipWrapper placement="top" tooltipText="Add new tag">
                        <AddTagButton className="add-tag-button" ref={addButtonRef} onClick={(e) => { e.stopPropagation(); setShowDropdown(v => !v); }}>
                            <SvgFragment type="plusTags" />
                        </AddTagButton>
                    </TooltipWrapper>
                    {showDropdown && (
                        usePortal ? (
                            createPortal(
                                    <PortalDropdownMenu
                                        ref={portalDropdownRef}
                                        style={{
                                            position: 'fixed',
                                            top: `${dropdownPosition.y}px`,
                                            left: `${dropdownPosition.x}px`,
                                            width: `${dropdownPosition.width}px`,
                                            zIndex: 10050,
                                            visibility: dropdownPosition.positioned ? 'visible' : 'hidden',
                                            pointerEvents: dropdownPosition.positioned ? 'auto' : 'none'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={handleKeyDown}
                                        tabIndex={0}
                                    >
                                        {renderDropdownContent()}
                                    </PortalDropdownMenu>,
                                    document.body
                                )
                            ) : (
                            <DropdownMenu ref={dropdownRef} $bulk={false} $below={dropdownBelow} onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown} tabIndex={0}>
                                {renderDropdownContent()}
                            </DropdownMenu>
                        )
                    )}
                </>
            )}
        </TagRow>
        </WidthLimiter>
    )
}

const TagRow = styled(Row)<{ $nowrap?: boolean, $hoverAdd?: boolean, $dropdownOpen?: boolean, $fullWidth?: boolean, $inboxTagSurface?: boolean}>`
    flex-wrap: ${({ $nowrap }) => ($nowrap ? 'nowrap' : 'wrap')};
    align-items: center;
    gap: 5px;
    border: none;
    background: none;
    padding: 0;
    position: ${({ $fullWidth }) => $fullWidth ? 'static' : 'relative'};
    ${({ $fullWidth }) => $fullWidth && 'width: 100%;'}
    ${({ $nowrap }) => $nowrap && `
        white-space: nowrap;
        overflow: visible;
    `}

    ${({ $hoverAdd, $dropdownOpen }) => $hoverAdd && `
        .add-tag-button { opacity: ${$dropdownOpen ? 1 : 0}; pointer-events: ${$dropdownOpen ? 'auto' : 'none'}; transition: opacity .15s ease-in-out; }
        &:hover .add-tag-button { opacity: 1; pointer-events: auto; }
    `}

    ${({ $inboxTagSurface }) => $inboxTagSurface && `
        background: ${styles.colors.white};
        border-radius: 4px;
        padding: 8px 10px;
    `}
`

const WidthLimiter = styled.div<{ $width?: number }>`
    width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
    max-width: 100%;
    overflow: visible;
`

const AddTagButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 0 4px;
    height: 28px;
    flex: 0 0 auto;
    svg {
        width: 18px;
        height: 18px;
    }
`

const DropdownMenu = styled.div<{ $bulk?: boolean; $below?: boolean; $fit?: boolean; $fullWidth?: boolean }>`
    position: absolute;
    ${({ $fullWidth, $fit }) => $fullWidth
        ? `left: 0; right: auto;`
        : ($fit ? `right: 0; left: auto;` : `left: 0; right: 0;`)}
    ${({ $bulk, $below }) => ($below || $bulk)
        ? `top: 100%; margin-top: 4px; bottom: unset; margin-bottom: 0;`
        : `bottom: 100%; margin-bottom: 4px; top: unset; margin-top: 0;`}
    z-index: 1000;
    background: white;
    border: 1px solid #eee;
    border-radius: 4px;
    ${({ $fullWidth, $fit }) => $fullWidth 
        ? `min-width: 300px; width: 100%; max-width: none;`
        : ($fit 
            ? `min-width: 300px; width: auto; max-width: 420px;`
            : `min-width: 160px; width: 100%; max-width: 100%;`)}
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    padding: 16px;
    display: flex;
    flex-direction: column;
    word-wrap: break-word;
    overflow-wrap: break-word;
`

const PortalDropdownMenu = styled.div`
    background: white;
    border: 1px solid #eee;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    padding: 16px;
    display: flex;
    flex-direction: column;
    word-wrap: break-word;
    overflow-wrap: break-word;
    box-sizing: border-box;
`

const HiddenMeasure = styled.div`
    position: fixed;
    top: -9999px;
    left: -9999px;
    visibility: hidden;
    pointer-events: none;
    display: flex;
    flex-direction: row;
    gap: 5px;
    width: max-content;
    height: auto;
    z-index: -1;
`

const DropdownSearchInput = styled.input`
    width: 100%;
    box-sizing: border-box;
    border: none;
    border-bottom: 1px solid #eee;
    outline: none;
    padding: 10px 12px;
    font-size: 14px;
    background: #fafbfc;
    border-radius: 4px 4px 0 0;
    margin-bottom: 16px;
    position: sticky;
    top: 0;
    z-index: 1;
`

const DropdownList = styled.div`
    min-height: 200px;
    max-height: 260px;
    overflow-y: auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-top: 16px;
    gap: 4px;
`

const DropdownItem = styled.div`
    padding: 6px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    &:hover {
        background: #f5f5f5;
    }
`

const TagRowItem = styled.div`
    padding: 3px 4px;
    display: flex;
    align-items: center;
    gap: 8px;
`

const CreateWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 4px 0 8px 0;
    margin-bottom: 16px;
    border-bottom: 1px solid ${styles.colors.black200};
`

const CreateButton = styled.button`
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0px;
    text-align: center;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 4px;
    color: ${({ theme: { secondaryColor } }) => secondaryColor};
    background-color: ${styles.colors.primary600};
    border: none;
`

const NoTagsWrapper = styled.div<{ $inboxSurface?: boolean }>`
    width: 100%;
    min-height: 86px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    background: ${({ $inboxSurface }) => ($inboxSurface ? styles.colors.white : styles.colors.black100)};
    border-radius: 4px;
    padding: 16px 0;
`

const NoTagsLabel = styled.div`
    font-weight: 700;
    line-height: 14px;
    font-size: 11px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${styles.colors.black300};
    font-family: NunitoSans;
`

const AddTagEmptyButton = styled.button`
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0px;
    text-align: center;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 4px;
    color: ${({theme: {secondaryColor}}) => secondaryColor};
    background-color: ${styles.colors.primary600};
    border: none;
`

const MoreTagWrapper = styled.div`
    position: relative;
    display: inline-block;
`

const MoreDropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 1000;
    background: white;
    border: 1px solid #eee;
    border-radius: 4px;
    min-width: 160px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    padding: 8px;
    margin-top: 4px;
`

const MoreDropdownList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const MoreDropdownItem = styled.div`
    display: flex;
    align-items: center;
`

const RemoveAllAction = styled.button<{ disabled?: boolean }>`
    background: none;
    border: none;
    color: ${styles.colors.red600};
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
    font-weight: 700;
`

export default ApplyRemoveTagForm
